import { apiClient } from "./api";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Problem filters interface
 */
export interface ProblemFilters {
  status?: string;
  priority?: string;
  limit?: number;
}

/**
 * Problem interface
 */
export interface Problem {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  rootCause?: string;
  solution?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  [key: string]: any;
}

/**
 * Problem statistics interface
 */
export interface ProblemStats {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  [key: string]: any;
}

/**
 * RCA Agent interface
 */
export interface RCAAgent {
  id: string;
  name: string;
  description: string;
  status: string;
  accuracy?: number;
  [key: string]: any;
}

/**
 * RCA Analysis result
 */
export interface RCAAnalysis {
  problemId: string;
  agentId: string;
  analysis: string;
  confidence: number;
  rootCause: string;
  recommendations: string[];
  [key: string]: any;
}

// ============================================================================
// API Functions
// ============================================================================

export const problemApi = {
  /**
   * Get all problems with filtering
   */
  getProblems: async (filters: ProblemFilters = {}): Promise<Problem[]> => {
    const params: Record<string, any> = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.limit) params.limit = filters.limit;

    const response = await apiClient.get<Problem[]>("/problems", { params });
    return response.data;
  },

  /**
   * Get problem statistics
   */
  getProblemStats: async (): Promise<ProblemStats> => {
    const response = await apiClient.get<ProblemStats>("/problems/stats");
    return response.data;
  },

  /**
   * Get problem by ID
   */
  getProblem: async (problemId: string): Promise<Problem> => {
    const response = await apiClient.get<Problem>(`/problems/${problemId}`);
    return response.data;
  },

  /**
   * Create a new problem
   */
  createProblem: async (problem: Partial<Problem>): Promise<Problem> => {
    const response = await apiClient.post<Problem>("/problems", problem);
    return response.data;
  },

  /**
   * Update problem
   */
  updateProblem: async (problemId: string, updates: Partial<Problem>): Promise<Problem> => {
    const response = await apiClient.put<Problem>(`/problems/${problemId}`, updates);
    return response.data;
  },

  /**
   * Create mock data for testing
   */
  createMockProblems: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/problems");
    return response.data;
  },
};

export const rcaApi = {
  /**
   * Get RCA agents
   */
  getRCAAgents: async (): Promise<RCAAgent[]> => {
    const response = await apiClient.get<RCAAgent[]>("/rca-agents");
    return response.data;
  },

  /**
   * Get RCA agent details
   */
  getRCAAgentDetails: async (agentId: string): Promise<RCAAgent> => {
    const response = await apiClient.get<RCAAgent>(`/rca-agents/${agentId}`);
    return response.data;
  },

  /**
   * Create RCA agent
   */
  createRCAAgent: async (agent: Partial<RCAAgent>): Promise<RCAAgent> => {
    const response = await apiClient.post<RCAAgent>("/rca-agents", agent);
    return response.data;
  },

  /**
   * Update RCA agent
   */
  updateRCAAgent: async (agentId: string, updates: Partial<RCAAgent>): Promise<RCAAgent> => {
    const response = await apiClient.put<RCAAgent>(`/rca-agents/${agentId}`, updates);
    return response.data;
  },

  /**
   * Perform RCA analysis
   */
  performRCAAnalysis: async (problemId: string, rcaAgentId: string): Promise<RCAAnalysis> => {
    const response = await apiClient.post<RCAAnalysis>(
      `/problems/${problemId}/rca-analysis`,
      null,
      {
        params: { rca_agent_id: rcaAgentId },
      }
    );
    return response.data;
  },

  /**
   * Auto-detect problems
   */
  autoDetectProblems: async (agentId: string): Promise<any> => {
    const response = await apiClient.post(`/rca-agents/${agentId}/detect-problems`);
    return response.data;
  },

  /**
   * Create mock RCA agents
   */
  createMockRCAAgents: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/rca-agents");
    return response.data;
  },
};

// ============================================================================
// Constants
// ============================================================================

export const PROBLEM_PRIORITIES = {
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
} as const;

export const PROBLEM_STATUSES = {
  NEW: "NEW",
  ANALYSIS_IN_PROGRESS: "ANALYSIS_IN_PROGRESS",
  ROOT_CAUSE_IDENTIFIED: "ROOT_CAUSE_IDENTIFIED",
  SOLUTION_PROPOSED: "SOLUTION_PROPOSED",
  SOLUTION_IMPLEMENTED: "SOLUTION_IMPLEMENTED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
} as const;

export const ROOT_CAUSE_CATEGORIES = {
  SYSTEM_DESIGN: "SYSTEM_DESIGN",
  CONFIGURATION_ERROR: "CONFIGURATION_ERROR",
  HUMAN_ERROR: "HUMAN_ERROR",
  PROCESS_FAILURE: "PROCESS_FAILURE",
  HARDWARE_FAILURE: "HARDWARE_FAILURE",
  SOFTWARE_BUG: "SOFTWARE_BUG",
  CAPACITY_ISSUE: "CAPACITY_ISSUE",
  SECURITY_VULNERABILITY: "SECURITY_VULNERABILITY",
  THIRD_PARTY_DEPENDENCY: "THIRD_PARTY_DEPENDENCY",
  UNKNOWN: "UNKNOWN",
} as const;

export const STATUS_LABELS: Record<string, string> = {
  [PROBLEM_STATUSES.NEW]: "New",
  [PROBLEM_STATUSES.ANALYSIS_IN_PROGRESS]: "Analysis in Progress",
  [PROBLEM_STATUSES.ROOT_CAUSE_IDENTIFIED]: "Root Cause Identified",
  [PROBLEM_STATUSES.SOLUTION_PROPOSED]: "Solution Proposed",
  [PROBLEM_STATUSES.SOLUTION_IMPLEMENTED]: "Solution Implemented",
  [PROBLEM_STATUSES.CLOSED]: "Closed",
  [PROBLEM_STATUSES.CANCELLED]: "Cancelled",
};

export const ROOT_CAUSE_LABELS: Record<string, string> = {
  [ROOT_CAUSE_CATEGORIES.SYSTEM_DESIGN]: "System Design",
  [ROOT_CAUSE_CATEGORIES.CONFIGURATION_ERROR]: "Configuration Error",
  [ROOT_CAUSE_CATEGORIES.HUMAN_ERROR]: "Human Error",
  [ROOT_CAUSE_CATEGORIES.PROCESS_FAILURE]: "Process Failure",
  [ROOT_CAUSE_CATEGORIES.HARDWARE_FAILURE]: "Hardware Failure",
  [ROOT_CAUSE_CATEGORIES.SOFTWARE_BUG]: "Software Bug",
  [ROOT_CAUSE_CATEGORIES.CAPACITY_ISSUE]: "Capacity Issue",
  [ROOT_CAUSE_CATEGORIES.SECURITY_VULNERABILITY]: "Security Vulnerability",
  [ROOT_CAUSE_CATEGORIES.THIRD_PARTY_DEPENDENCY]: "Third Party Dependency",
  [ROOT_CAUSE_CATEGORIES.UNKNOWN]: "Unknown",
};

// Type exports for constants
export type ProblemPriority = (typeof PROBLEM_PRIORITIES)[keyof typeof PROBLEM_PRIORITIES];
export type ProblemStatus = (typeof PROBLEM_STATUSES)[keyof typeof PROBLEM_STATUSES];
export type RootCauseCategory = (typeof ROOT_CAUSE_CATEGORIES)[keyof typeof ROOT_CAUSE_CATEGORIES];
